import { useEffect, useState } from "react";
import type { PageData, SiteData } from "../shared/types";
import { pageDataUrl, siteDataUrl } from "../shared/paths";

/**
 * Reads the files the build wrote (`scripts/gen-docs.mjs`). Each file is fetched once per page load
 * and kept in memory; nothing about the docs is fetched from the API at runtime.
 */
type Load<T> = { status: "loading" } | { status: "ready"; data: T } | { status: "missing" } | { status: "error" };

const cache = new Map<string, Promise<unknown>>();

export const loadJson = <T>(url: string): Promise<T> => {
  if (!cache.has(url)) {
    cache.set(
      url,
      fetch(url).then((res) => {
        if (res.status === 404) return null;
        if (!res.ok) throw new Error(`${res.status} ${url}`);
        if (!(res.headers.get("content-type") ?? "").includes("json")) return null;

        return res.json();
      })
    );
  }

  return cache.get(url) as Promise<T>;
};

/** Tests start from an empty cache. */
export const clearDocsCache = () => cache.clear();

const useJson = <T>(url: string | null): Load<T> => {
  const [state, setState] = useState<Load<T>>({ status: "loading" });

  useEffect(() => {
    if (!url) {
      setState({ status: "missing" });

      return;
    }
    let current = true;
    setState({ status: "loading" });
    loadJson<T | null>(url)
      .then((data) => {
        if (current) setState(data === null ? { status: "missing" } : { status: "ready", data });
      })
      .catch(() => {
        if (current) setState({ status: "error" });
      });

    return () => {
      current = false;
    };
  }, [url]);

  return state;
};

export const useSite = () => useJson<SiteData>(siteDataUrl());

export const usePage = (path: string | null) => useJson<PageData>(path ? pageDataUrl(path) : null);
